/**
 * main.tsx — Admin panel entry point.
 *
 * Bootstraps React onto `#root` and wires up every cross-cutting provider the
 * app depends on. Provider stack (outermost -> innermost):
 *  - `React.StrictMode`
 *  - `QueryClientProvider` — shared React Query client (config below)
 *  - `ThemeModeProvider` — light/dark mode preference (our own context)
 *  - `AppWrapper` -> `CacheProvider` (Emotion, RTL-aware) -> `ThemeProvider`
 *    (MUI theme) -> `LocalizationProvider` (date pickers) -> `BrowserRouter`
 *    -> `App`
 *
 * Also initializes i18n via the bare `import './i18n'` side-effect import, and
 * keeps the document direction/lang and the Day.js locale in sync with the
 * active language for full bilingual EN/AR + RTL support.
 */
import React, { useMemo, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/ar';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { useTranslation } from 'react-i18next';
import App from './App';
import { createAppTheme } from './theme';
import { ThemeModeProvider, useThemeMode } from './contexts/ThemeModeContext';

import './i18n';

/**
 * Single shared React Query client for the whole admin panel. Defaults are
 * tuned for a data-heavy admin tool: data stays fresh briefly, is cached a few
 * minutes, retries once, and does not refetch on window focus.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,      // treat fetched data as fresh for 1 minute
      gcTime: 5 * 60_000,     // keep unused data in cache for 5 minutes
      retry: 1,               // fail fast instead of retrying 3× by default
      refetchOnWindowFocus: false, // don't refetch just because the tab regained focus
    },
  },
});

/**
 * Builds the language/theme-aware provider shell around `<App />`.
 *
 * Reacts to two inputs:
 *  - the active i18n language (`ar` => RTL, anything else => LTR), and
 *  - the current theme `mode` from `ThemeModeContext` (light/dark).
 *
 * From those it derives: the MUI theme (`createAppTheme`), an Emotion cache
 * configured with the RTL stylis plugin when needed, and the Day.js picker
 * locale. Renders the Emotion `CacheProvider`, MUI `ThemeProvider`, date-picker
 * `LocalizationProvider`, `CssBaseline`, and the `BrowserRouter` wrapping `App`.
 */
function AppWrapper() {
  const { i18n } = useTranslation();
  const direction = i18n.language === 'ar' ? 'rtl' : 'ltr';
  const { mode } = useThemeMode();
  // Recompute the MUI theme only when direction or light/dark mode changes.
  const theme = useMemo(() => createAppTheme(direction, mode), [direction, mode]);

  // Emotion cache must be rebuilt when direction flips so the RTL stylis plugin
  // (which rewrites left/right CSS) is added or removed accordingly.
  const emotionCache = useMemo(
    () =>
      createCache({
        key: direction === 'rtl' ? 'muirtl' : 'mui',
        prepend: true,
        stylisPlugins: direction === 'rtl' ? [prefixer, rtlPlugin] : [prefixer],
      }),
    [direction],
  );

  const pickerLocale = i18n.language === 'ar' ? 'ar' : 'en';

  // Keep the <html> dir/lang attributes and the global Day.js locale in sync
  // with the active language so native RTL, screen readers, and date
  // formatting all follow the chosen language.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('dir', direction);
    root.setAttribute('lang', i18n.language);
    dayjs.locale(pickerLocale);
  }, [direction, i18n.language, pickerLocale]);

  return (
    <CacheProvider value={emotionCache} key={direction}>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={pickerLocale}>
          <CssBaseline />
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </LocalizationProvider>
      </ThemeProvider>
    </CacheProvider>
  );
}

// Mount the app on #root with the global providers that sit ABOVE the
// language/theme shell (StrictMode, React Query, theme-mode context). The
// language/theme-dependent providers live inside AppWrapper.
ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeModeProvider>
        <AppWrapper />
      </ThemeModeProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);

